import { useCallback, useContext, useMemo } from 'react';
import { isConfigReport, isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
// @ts-expect-error UnTyped dependency
import { loadResolve } from '../../../reports/decorators/DataDecorator/loadResolve';
import { useAsyncFetcher } from 'reporting-data/asyncData/useAsyncFetcher';
import { isFailed, isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { RelationalReport } from 'reporting-snowflake/relational/schema/report-records';
import useSnowflakeRaaS from '../../snowflake/useSnowflakeRaaS';
import { ReportWithOptions } from 'reporting-snowflake/relational/resolve/resolve-records';
// @ts-expect-error UnTyped dependency
import { createDefaultReportOptions } from './../../snowflake/report-options';
import { fromJS } from 'immutable';
import useMeta from './../../snowflake/internal/useMeta';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';

/**
 *  Given a SF dataset, fix the structure of the dataset so that it is consistent between SF and SO datasets
 *  We should ask BE to return the data in a consistent format
 */
const fixSnowflakeDataset = dataset => {
  if (!dataset) {
    return null;
  }

  // fix the structure of the dataset by wrapping it with "Primary", "data" property
  const newDataSet = fromJS({
    primary: dataset
  });
  return newDataSet;
};
const useQueryConfigRaas = ({
  report
}) => {
  const fetcher = useCallback(async () => {
    const {
      resolve
    } = await loadResolve();
    return resolve(report);
  }, [report]);
  const response = useAsyncFetcher({
    fetcher
  });
  const data = isSucceeded(response) ? response.data : null;
  const error = isFailed(response) ? response.error : null;
  const loading = isLoading(response);
  return {
    data,
    loading,
    error,
    metaStateData: null
  };
};
const useQuerySnowflakeRaas = ({
  report
}) => {
  const {
    gates
  } = useContext(UserInfoContext);
  const reportDefinition = useMemo(() => {
    var _report$get;
    return RelationalReport(report === null || report === void 0 || (_report$get = report.get('reportDefinition')) === null || _report$get === void 0 ? void 0 : _report$get.toJS());
  }, [report]);
  const reportWithOptions = useMemo(() => ReportWithOptions({
    reportDefinition,
    reportOptions: createDefaultReportOptions(reportDefinition)
  }), [reportDefinition]);

  // we might need to applu some 'skip' login into this useSnowflakeRaaS hook
  // so that it will not do any uncessary calculation ie: returning visualization object
  const snowflakeDataset = useSnowflakeRaaS(reportWithOptions, {}, gates);

  // not sure yet if this is a good place to have this here
  const metaState = useMeta(reportDefinition);
  const fixedData = useMemo(() => snowflakeDataset.dataset ? fixSnowflakeDataset(snowflakeDataset.dataset) : null, [snowflakeDataset.dataset]);
  const loading = snowflakeDataset.loading || metaState.loading;
  const error = snowflakeDataset.error || metaState.error;
  return {
    data: fixedData,
    metaStateData: metaState.data,
    loading,
    error
  };
};
const useUnsupportedReportType = () => {
  return useMemo(() => ({
    error: new Error('Unsupported Report Type in useResolveData'),
    loading: false,
    data: null,
    metaStateData: null
  }), []);
};

/**
 * Given a report, fetch the data and return the dataset
 * We can also use this hook to reformat data between SF and SO so that they are consistent
 */
export const useResolveData = ({
  report
}) => {
  let useFetchData;
  if (isConfigReport(report)) {
    useFetchData = useQueryConfigRaas;
  } else if (isRelationalReport(report)) {
    useFetchData = useQuerySnowflakeRaas;
  } else {
    useFetchData = useUnsupportedReportType;
  }
  const dataset = useFetchData({
    report: report
  });
  return dataset;
};